import type { ShipClassId } from '@brig/rules-engine';
import manifest from '../assets/ships/tokens/manifest.json';
import vectorFlagPoints from './tokens/flag-points.json';
import * as source from './sourceArt.ts';

/**
 * Which ship artwork the client shows (build-time, VITE_SHIP_ART):
 * - `source`: cards and map tokens cut from assets/source/ships-original.png.
 * - `fallback`: our own vector tokens; cards show a large token instead of the illustration.
 * The game is fully playable in both.
 */
export type ShipArt = 'source' | 'fallback';

export function parseShipArt(value: string | undefined): ShipArt {
  return value === 'fallback' ? 'fallback' : 'source';
}

export const SHIP_ART: ShipArt = parseShipArt(import.meta.env.VITE_SHIP_ART);

/** The art layer actually used: `source` only when it was requested and is in the build. */
export function effectiveArt(art: ShipArt): ShipArt {
  return art === 'source' && hasSourceArt ? 'source' : 'fallback';
}

/** Below this rendered size (CSS px) raster tokens turn to mush; the vector token is used instead. */
export const MIN_RASTER_TOKEN_PX = 32;

/** Width/height of each cut-out ship inside its square token image. */
export function tokenAspect(id: ShipClassId): number {
  return manifest[id].aspect;
}

/** Whether the `source` layer is in this build; without it everything falls back to vector art. */
export const hasSourceArt = source.available;

/** Exported token sizes (CSS px); each also exists at 2x. */
const TOKEN_SIZES = [32, 48, 64, 96] as const;
const variants = TOKEN_SIZES.flatMap((size) => [
  { px: size, name: `${size}` },
  { px: size * 2, name: `${size}@2x` },
]).sort((a, b) => a.px - b.px);

/** URL of the smallest token image that covers `cssPx` at the given device pixel ratio. */
export function tokenUrl(id: ShipClassId, cssPx: number, dpr = 1): string {
  const need = cssPx * dpr;
  const pick = variants.find((v) => v.px >= need) ?? variants[variants.length - 1];
  const url = source.tokenFiles[`../assets/ships/tokens/${id}-${pick?.name ?? '96@2x'}.webp`];
  if (!url) throw new Error(`missing token image for ${id}`);
  return url;
}

/** Tiny blurred preview of a card illustration, as a data: URL. */
export function cardPlaceholder(id: ShipClassId): string | undefined {
  return source.cardPlaceholders[id];
}

export function cardUrl(id: ShipClassId, scale: 1 | 2, format: 'webp' | 'avif'): string {
  const url = source.cardFiles[`../assets/ships/cards/${id}@${scale}x.${format}`];
  if (!url) throw new Error(`missing card image for ${id}`);
  return url;
}

export type FlagPoint = readonly [number, number];
export interface FlagPoints {
  readonly main: FlagPoint;
  readonly stern?: FlagPoint;
}

/**
 * Where the nation's flags fly, in 0..1 of the ship image: the raster cut-out (from the
 * slicer's manifest) or the 32×32 vector symbol.
 */
export function flagPoints(id: ShipClassId, raster: boolean): FlagPoints {
  if (raster) {
    const f = manifest[id].flags as { main: number[]; stern?: number[] };
    const pt = (p: number[]): FlagPoint => [p[0] ?? 0.5, p[1] ?? 0];
    return f.stern ? { main: pt(f.main), stern: pt(f.stern) } : { main: pt(f.main) };
  }
  const v = vectorFlagPoints[id].main;
  return { main: [v[0] ?? 0.5, v[1] ?? 0] };
}
